class SeeInActionModal {
  constructor(modalId) {
    this.modalId = modalId;
    this.button = document.querySelector(`[data-modal-id="${modalId}"]`);
    this.modal = document.getElementById(modalId);
    
    if (!this.button || !this.modal) return;
    
    this.videoSwiper = null;
    this.scrollPosition = 0;
    this.bodyPaddingRight = 0;
    this.isInitialized = false;
    
    this.init();
  }
  
  init() {
    this.bindEvents();
  }
  
  bindEvents() {
    this.button.addEventListener('click', (e) => this.handleOpen(e));
    
    const closeBtn = this.modal.querySelector('.see-in-action-modal__close');
    const overlay = this.modal.querySelector('.see-in-action-modal__overlay');
    const content = this.modal.querySelector('.see-in-action-modal__content');
    
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });
    }
    
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.close();
        }
      });
    }
    
    if (content) {
      content.addEventListener('click', (e) => e.stopPropagation());
    }
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.modal.hidden) {
        this.close();
      }
    });
  }
  
  handleOpen(e) {
    e.preventDefault();
    e.stopPropagation();
    
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    this.bodyPaddingRight = parseInt(window.getComputedStyle(document.body).paddingRight) || 0;
    
    this.modal.hidden = false;
    this.lockBody(scrollbarWidth);
    
    if (!this.videoSwiper) {
      requestAnimationFrame(() => {
        this.initSwiper();
        setTimeout(() => this.playFirstVideo(), 300);
      });
    } else {
      setTimeout(() => this.playFirstVideo(), 200);
    }
  }
  
  lockBody(scrollbarWidth) {
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${this.scrollPosition}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${this.bodyPaddingRight + scrollbarWidth}px`;
    }
  }
  
  unlockBody() {
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.overflow = '';
    body.style.paddingRight = '';
    window.scrollTo(0, this.scrollPosition);
  }
  
  initSwiper() {
    const slider = this.modal.querySelector('.see-in-action-modal__slider');
    if (!slider || typeof Swiper === 'undefined' || this.videoSwiper) return;
    
    const prevBtn = slider.querySelector('.see-in-action-modal__nav-prev');
    const nextBtn = slider.querySelector('.see-in-action-modal__nav-next');
    
    this.videoSwiper = new Swiper(slider, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      autoHeight: false,
      grabCursor: true,
      allowTouchMove: true,
      touchRatio: 1,
      touchAngle: 45,
      simulateTouch: true,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      on: {
        init: () => {
          const content = this.modal.querySelector('.see-in-action-modal__content');
          if (content) {
            content.style.minHeight = '400px';
          }
          this.updateNavigationButtons();
        }
      }
    });
    
    this.videoSwiper.on('slideChange', (swiper) => {
      this.updateNavigationButtons();
      this.pauseAllVideos();
      this.playActiveVideo(swiper);
    });
    
    requestAnimationFrame(() => this.updateNavigationButtons());
  }
  
  updateNavigationButtons() {
    if (!this.videoSwiper) return;
    
    const slider = this.modal.querySelector('.see-in-action-modal__slider');
    const prevBtn = slider?.querySelector('.see-in-action-modal__nav-prev');
    const nextBtn = slider?.querySelector('.see-in-action-modal__nav-next');
    
    if (!prevBtn || !nextBtn) return;
    
    const hasMultipleSlides = this.videoSwiper.slides?.length > 1;
    
    if (!hasMultipleSlides) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
      prevBtn.style.visibility = 'hidden';
      nextBtn.style.visibility = 'hidden';
      return;
    }
    
    prevBtn.style.display = 'flex';
    nextBtn.style.display = 'flex';
    prevBtn.style.visibility = 'visible';
    nextBtn.style.visibility = 'visible';
    
    prevBtn.classList.toggle('swiper-button-disabled', this.videoSwiper.isBeginning);
    nextBtn.classList.toggle('swiper-button-disabled', this.videoSwiper.isEnd);
  }
  
  pauseAllVideos() {
    const videoElements = this.modal.querySelectorAll('video-element');
    videoElements.forEach(videoEl => {
      if (typeof videoEl.pause === 'function') {
        videoEl.pause();
      }
      const video = videoEl.querySelector('video');
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }
  
  playActiveVideo(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    const videoElement = activeSlide?.querySelector('video-element');
    if (videoElement) {
      setTimeout(() => this.loadAndPlayVideo(videoElement), 200);
    }
  }
  
  playFirstVideo() {
    if (!this.videoSwiper) return;
    const firstSlide = this.videoSwiper.slides[0];
    const videoElement = firstSlide?.querySelector('video-element');
    if (videoElement) {
      this.loadAndPlayVideo(videoElement);
    }
  }
  
  loadAndPlayVideo(videoElement) {
    if (!videoElement) return;
    
    if (videoElement.hasAttribute('loaded')) {
      if (typeof videoElement.play === 'function') {
        videoElement.play();
      } else {
        const video = videoElement.querySelector('video');
        video?.play().catch(() => {});
      }
      return;
    }
    
    const posterButton = videoElement.querySelector('[id^="DeferredPoster-"]');
    if (posterButton) {
      setTimeout(() => posterButton.click(), 100);
      return;
    }
    
    if (videoElement.player) {
      videoElement.player
        .then(() => {
          setTimeout(() => {
            if (typeof videoElement.play === 'function') {
              videoElement.play();
            }
          }, 200);
        })
        .catch(() => {
          const video = videoElement.querySelector('video');
          video?.play().catch(() => {});
        });
    }
  }
  
  close(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    this.modal.hidden = true;
    this.unlockBody();
    this.pauseAllVideos();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const modals = document.querySelectorAll('[id^="SeeInActionModal-"]');
  modals.forEach(modal => {
    const modalId = modal.id;
    new SeeInActionModal(modalId);
  });
});
